import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from '../../i18n/useTranslation';
import { Dialog } from '../ui/Dialog/Dialog';
import { Button } from '../atoms/Button/Button';
import { Input } from '../atoms/Input/Input';
import { Alert } from '../atoms/Alert/Alert';
import { Stack } from '../atoms/Stack/Stack';
import { Inline } from '../atoms/Inline/Inline';
import type { HistoryEntry, MeasurementUpdate } from '../../services/measurementService';
import { parameterLabel, toDateTimeLocalValue } from './measurementHistoryUtils';

export interface EditMeasurementDialogProps {
  /** The measurement to correct; null closes the dialog. */
  entry: HistoryEntry | null;
  isSaving: boolean;
  saveFailed: boolean;
  onSave: (entry: HistoryEntry, update: MeasurementUpdate) => void;
  onClose: () => void;
}

/** Correct value and/or time of a manual or photo measurement. Parameter and unit stay. */
export function EditMeasurementDialog({ entry, isSaving, saveFailed, onSave, onClose }: EditMeasurementDialogProps) {
  const { t } = useTranslation();
  const [value, setValue] = useState('');
  const [measuredAt, setMeasuredAt] = useState('');
  const [valueError, setValueError] = useState<string | undefined>();
  const [dateError, setDateError] = useState<string | undefined>();

  useEffect(() => {
    if (!entry) return;
    setValue(String(entry.value));
    setMeasuredAt(toDateTimeLocalValue(entry.measuredAt));
    setValueError(undefined);
    setDateError(undefined);
  }, [entry]);

  if (!entry) return null;

  const formId = `edit-measurement-${entry.id}`;
  const label = parameterLabel(t, entry.parameterId, entry.parameterName);

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const parsed = Number(value.replace(',', '.'));
    const date = new Date(measuredAt);
    const nextValueError = value.trim() === '' || !Number.isFinite(parsed) || parsed < 0 ? t('measurement.history.edit.valueInvalid') : undefined;
    const nextDateError = measuredAt === '' || Number.isNaN(date.getTime()) ? t('measurement.history.edit.dateInvalid') : undefined;
    setValueError(nextValueError);
    setDateError(nextDateError);
    if (nextValueError || nextDateError) return;
    onSave(entry, { value: parsed, measuredAt: date.toISOString() });
  };

  return (
    <Dialog
      isOpen
      onClose={onClose}
      title={t('measurement.history.edit.title', { parameter: label })}
      size="sm"
      data-testid="edit-measurement-dialog"
      footer={
        <Inline gap="2" justify="end" wrap>
          <Button variant="secondary" onClick={onClose} disabled={isSaving}>
            {t('measurement.history.edit.cancel')}
          </Button>
          <Button type="submit" form={formId} isLoading={isSaving}>
            {t('measurement.history.edit.save')}
          </Button>
        </Inline>
      }
    >
      <form id={formId} onSubmit={handleSubmit} noValidate>
        <Stack gap="4">
          {saveFailed && <Alert tone="danger">{t('measurement.history.edit.saveFailed')}</Alert>}
          <Input
            label={entry.unit ? `${t('measurement.history.edit.value')} (${entry.unit})` : t('measurement.history.edit.value')}
            type="text"
            inputMode="decimal"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            error={valueError}
            fullWidth
            required
          />
          <Input
            label={t('measurement.history.edit.measuredAt')}
            type="datetime-local"
            value={measuredAt}
            onChange={(e) => setMeasuredAt(e.target.value)}
            error={dateError}
            fullWidth
            required
          />
        </Stack>
      </form>
    </Dialog>
  );
}

export default EditMeasurementDialog;
